import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon, XIcon, MoreVerticalIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../types/Transaction';
import { RefundModal } from '../components/RefundModal';
import { buildMockSales } from '../utils/mockSales';
import { formatMoney } from '../utils/money';
export function Sales() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedSaleForRefund, setSelectedSaleForRefund] = useState<Sale | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Use shared mock sales fixtures (single source of truth)
  const sales: Sale[] = useMemo(() => buildMockSales(), []);
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sourceFilter !== 'all' || paymentMethodFilter !== 'all' || dateFrom !== '' || dateTo !== '' || amountMin !== '' || amountMax !== '';
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-10617" />,
      qr_code: <QrCodeIcon size={16} data-id="element-10618" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-10619" />,
      invoice: <FileTextIcon size={16} data-id="element-10620" />,
      pos: <TerminalIcon size={16} data-id="element-10621" />,
      recurring: <RefreshCwIcon size={16} data-id="element-10622" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-10623" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getPaymentMethodCategory = (paymentMethod: Sale['paymentMethod']) => {
    const categories: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return categories[paymentMethod.type] || paymentMethod.type;
  };
  const getPaymentMethodName = (paymentMethod: Sale['paymentMethod']) => {
    if (paymentMethod.brand) return paymentMethod.brand;
    if (paymentMethod.details) return paymentMethod.details;
    const fallback: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return fallback[paymentMethod.type] || paymentMethod.type;
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-10624">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.paymentMethod.type === paymentMethodFilter;
    const saleDate = new Date(sale.date);
    const matchesDateFrom = !dateFrom || saleDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || saleDate <= new Date(dateTo + 'T23:59:59');
    const matchesAmountMin = !amountMin || sale.amount >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || sale.amount <= parseFloat(amountMax);
    return matchesSearch && matchesStatus && matchesSource && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  // Derive currency from first sale (assume single-currency list)
  const currency = filteredSales[0]?.currency || 'THB';
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: selectedSaleForRefund?.transactionId,
      amount,
      reason,
      notes
    });
    // In a real app, this would call an API to process the refund
    // For now, we'll just close the modal and show a success message
    alert(`Refund of ${formatMoney(amount, selectedSaleForRefund?.currency)} initiated successfully!`);
  };
  const handleOpenRefundModal = (sale: Sale) => {
    setSelectedSaleForRefund(sale);
    setRefundModalOpen(true);
  };
  return <div className="space-y-6" data-id="element-10625">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10626">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-10627">
          <div className="text-sm font-medium text-gray-500" data-id="element-10628">Total Volume</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-10629">
            {formatMoney(totalVolume, currency)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-10630">
          <div className="text-sm font-medium text-gray-500" data-id="element-10631">
            Total Transactions
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-10632">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-10633">
          <div className="text-sm font-medium text-gray-500" data-id="element-10634">Successful</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-10635">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-10636">
          <div className="text-sm font-medium text-gray-500" data-id="element-10637">Success Rate</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-10638">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-10639">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-10640">
          <div className="space-y-4" data-id="element-10641">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-10642">
              <div className="flex-1 max-w-lg" data-id="element-10643">
                <div className="relative" data-id="element-10644">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-10645" />
                  <input type="text" placeholder="Search by transaction ID, customer name, or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-10646" />
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-10647">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showAdvancedFilters ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} data-id="element-10648">
                  <FilterIcon size={18} data-id="element-10649" />
                  Filters
                  {hasActiveFilters && <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full" data-id="element-10650">
                      {[searchTerm, statusFilter !== 'all', sourceFilter !== 'all', paymentMethodFilter !== 'all', dateFrom, dateTo, amountMin, amountMax].filter(Boolean).length}
                    </span>}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-10651">
                  <DownloadIcon size={18} data-id="element-10652" />
                  Export
                </button>
              </div>
            </div>
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-id="element-10653">
                <div className="flex items-center justify-between" data-id="element-10654">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-10655">
                    Advanced Filters
                  </h3>
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-10656">
                      <XIcon size={16} data-id="element-10657" />
                      Clear all
                    </button>}
                </div>
                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10658">
                  {/* Status Filter */}
                  <div data-id="element-10659">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10660">
                      Status
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10661">
                      <option value="all" data-id="element-10662">All Status</option>
                      <option value="completed" data-id="element-10663">Completed</option>
                      <option value="pending" data-id="element-10664">Pending</option>
                      <option value="processing" data-id="element-10665">Processing</option>
                      <option value="failed" data-id="element-10666">Failed</option>
                      <option value="refunded" data-id="element-10667">Refunded</option>
                    </select>
                  </div>
                  {/* Source Filter */}
                  <div data-id="element-10668">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10669">
                      Source
                    </label>
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10670">
                      <option value="all" data-id="element-10671">All Sources</option>
                      <option value="payment_link" data-id="element-10672">Payment Link</option>
                      <option value="qr_code" data-id="element-10673">QR Code</option>
                      <option value="tap_to_phone" data-id="element-10674">Tap to Phone</option>
                      <option value="invoice" data-id="element-10675">Invoice</option>
                      <option value="pos" data-id="element-10676">POS Terminal</option>
                      <option value="recurring" data-id="element-10677">Recurring</option>
                      <option value="virtual_terminal" data-id="element-10678">Virtual Terminal</option>
                    </select>
                  </div>
                  {/* Payment Method Filter */}
                  <div data-id="element-10679">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10680">
                      Payment Method
                    </label>
                    <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10681">
                      <option value="all" data-id="element-10682">All Methods</option>
                      <option value="card" data-id="element-10683">Card</option>
                      <option value="wallet" data-id="element-10684">Wallet</option>
                      <option value="bank_transfer" data-id="element-10685">Bank Transfer</option>
                      <option value="cash" data-id="element-10686">Cash</option>
                    </select>
                  </div>
                  {/* Date From */}
                  <div data-id="element-10687">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10688">
                      Date From
                    </label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10689" />
                  </div>
                  {/* Date To */}
                  <div data-id="element-10690">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10691">
                      Date To
                    </label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10692" />
                  </div>
                  {/* Amount Min */}
                  <div data-id="element-10693">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10694">
                      Min Amount (THB)
                    </label>
                    <input type="number" placeholder="0" value={amountMin} onChange={e => setAmountMin(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10695" />
                  </div>
                  {/* Amount Max */}
                  <div data-id="element-10696">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-10697">
                      Max Amount (THB)
                    </label>
                    <input type="number" placeholder="No limit" value={amountMax} onChange={e => setAmountMax(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-10698" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-10699">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-10700">
            <thead className="bg-gray-50" data-id="element-10701">
              <tr data-id="element-10702">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10703">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10704">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10705">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10706">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10707">
                  Payment Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10708">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10709">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10710">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10711">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-10712">
              {filteredSales.map(sale => <tr key={sale.id} onClick={() => navigate(`/sale-detail/${sale.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-10713">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10714">
                    <div className="text-sm font-medium text-gray-900" data-id="element-10715">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10716">
                    <div className="text-sm text-gray-900" data-id="element-10717">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-10718">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10719">
                    <div className="text-sm text-gray-900" data-id="element-10720">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-10721">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10722">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-10723">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10724">
                    <div className="text-sm text-gray-900" data-id="element-10725">
                      {getPaymentMethodCategory(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10726">
                    <div className="text-sm text-gray-900" data-id="element-10727">
                      {getPaymentMethodName(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10728">
                    <div className="text-sm font-medium text-gray-900" data-id="element-10729">
                      {formatMoney(sale.amount, sale.currency)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10730">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" onClick={e => e.stopPropagation()} data-id="element-10731">
                    <div className="relative inline-block" data-id="element-10732">
                      <button onClick={() => setOpenMenuId(openMenuId === sale.id ? null : sale.id)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-10733">
                        <MoreVerticalIcon size={18} className="text-gray-600" data-id="element-10734" />
                      </button>

                      {/* Dropdown Menu */}
                      {openMenuId === sale.id && <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-10735" />
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-10736">
                            <button onClick={() => {
                        setOpenMenuId(null);
                        navigate(`/sale-detail/${sale.id}`);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-10737">
                              <EyeIcon size={16} data-id="element-10738" />
                              View details
                            </button>
                            {sale.status === 'completed' && <button onClick={() => {
                        setOpenMenuId(null);
                        handleOpenRefundModal(sale);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-10739">
                                <RefreshCwIcon size={16} data-id="element-10740" />
                                Refund
                              </button>}
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-10741">
            <p className="text-gray-500" data-id="element-10742">No transactions found</p>
          </div>}
      </div>
      {/* Refund Modal */}
      {selectedSaleForRefund && <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={selectedSaleForRefund.amount} transactionId={selectedSaleForRefund.transactionId} currency="THB" onRefund={handleRefund} data-id="element-10743" />}
    </div>;
}